// @ts-ignore
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from "axios";
import { hosts } from "./config";
import { CredifyConfig } from "../types";
import { camelize } from "./utils";
import { CredifyError, OIDCError } from "./errors";
import packageJson from "../package.json";

class ApiClient {
  public readonly instance: AxiosInstance;
  constructor(apiKey: string, accessToken: string, config: CredifyConfig) {
    const api = hosts(config).api;
    const reqObject = {
      baseURL: `${api}`,
      timeout: 8000,
      headers: {
        "Accept-Language": config.locale || "en-US",
      },
    };
    if (apiKey) {
      // @ts-ignore
      reqObject.headers["X-API-KEY"] = apiKey;
    }
    if (accessToken) {
      // @ts-ignore
      reqObject.headers["Authorization"] = accessToken;
    }
    // @ts-ignore
    reqObject.headers["User-Agent"] = "servicex/nodejs/" + packageJson.version;

    const instance = axios.create(reqObject);
    // *** Somehow the request interceptor block the response interceptor and we don't have a use case for using
    // the request interceptor yet so I temporary disable it first and comeback to this issue later when we have use case for it
    // instance.interceptors.request.use((request) => {
    //   return request;
    // });
    instance.interceptors.response.use((response) => {
      if (
        response.data &&
        response.headers &&
        response.headers["content-type"] === "application/json"
      ) {
        const url = response.config.url || "";
        // UserInfo response should not be using camelize.
        if (url.includes("userinfo")) return response;

        response.data = camelize(response.data);
      }
      return response;
    });
    this.instance = instance;
  }

  async get<T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.instance.get<T>(url, config).catch((e: AxiosError) => {
      throw this.generateError(url, e);
    });
  }

  async post<T>(
    url: string,
    body: object,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.instance.post<T>(url, body, config).catch((e: AxiosError) => {
      throw this.generateError(url, e);
    });
  }

  async formPost<T>(
    url: string,
    params: object,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    const p = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      // @ts-ignore
      p.append(key, params[key]);
    });
    const c = {
      headers: {
        "content-type": "application/x-www-form-urlencoded",
      },
    };
    return this.instance.post<T>(url, p, c).catch((e: AxiosError) => {
      throw this.generateError(url, e);
    });
  }

  async put<T>(
    url: string,
    body: object,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.instance.put<T>(url, body, config).catch((e: AxiosError) => {
      throw this.generateError(url, e);
    });
  }

  async delete<T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.instance.delete<T>(url, config).catch((e: AxiosError) => {
      throw this.generateError(url, e);
    });
  }

  generateError(url: string, error: AxiosError): Error {
    if (url.includes("oidc-core.credify")) {
      return new OIDCError(error);
    } else {
      return new CredifyError(error);
    }
  }
}

export default ApiClient;
